import { ChevronDown } from '@orgus/ui/icons';
import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuTrigger, Input, Pressable, Tooltip, cn } from '@orgus/ui';

/** The quiet value at rest: reads as text, shows it is editable on hover and focus. */
const VALUE = 'inline-flex h-control-sm max-w-full min-w-0 items-center gap-1 rounded-control px-2 text-ui text-fg-muted transition-colors duration-(--duration-fast) hover:bg-hover hover:text-fg';

/**
 * A property that reads as its value and becomes a text box on click or focus (inline edit):
 * Enter or leaving the box saves, Esc puts the value back. Empty shows `empty` in a quiet tone,
 * never a placeholder that looks like data. `validate` keeps the box open with its message
 * under it; `onCommit` throws to refuse (the value goes back).
 */
export function InlineText({
  value,
  empty,
  label,
  hint,
  onCommit,
  validate,
  display,
  numeric,
  mono,
  align = 'start',
  width = 'w-24',
  className,
}: {
  value: string;
  /** The value at rest, when it reads better than the raw text ("90%" for "90"). */
  display?: ReactNode;
  /** The problem with a typed value, in words, or null when it may be saved. */
  validate?: (text: string) => string | null;
  /** What an empty value reads as: "No limit", "Automatic, 16". */
  empty: string;
  /** The property's accessible name: "Session limit for studio". */
  label: string;
  /** Shown on hover of the value, where it prevents a mistake. */
  hint?: string;
  onCommit: (text: string) => void | Promise<void>;
  numeric?: boolean;
  mono?: boolean;
  align?: 'start' | 'end';
  /** The box's width while editing. */
  width?: string;
  className?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(value);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const done = useRef(false);
  useEffect(() => {
    if (!editing) setText(value);
  }, [value, editing]);

  const commit = async () => {
    if (done.current) return;
    done.current = true;
    if (text.trim() === value.trim()) {
      setError(null);
      setEditing(false);
      return;
    }
    const problem = validate?.(text.trim()) ?? null;
    if (problem) {
      setError(problem);
      done.current = false;
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await onCommit(text.trim());
    } catch {
      setText(value);
    } finally {
      setBusy(false);
      setEditing(false);
    }
  };

  if (editing) {
    return (
      <Input
        size="sm"
        autoFocus
        aria-label={label}
        inputMode={numeric ? 'numeric' : undefined}
        value={text}
        error={error ?? undefined}
        disabled={busy}
        onFocus={(e) => e.currentTarget.select()}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => void commit()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          if (e.key === 'Escape') {
            done.current = true;
            setError(null);
            setText(value);
            setEditing(false);
          }
        }}
        wrapperClassName={width}
        className={cn(numeric && 'num', mono && 'font-mono', align === 'end' && 'text-right')}
      />
    );
  }
  const shown = value.trim() ? value : null;
  const button = (
    <Pressable
      aria-label={`${label}: ${shown ?? empty}. Edit`}
      onClick={() => {
        done.current = false;
        setEditing(true);
      }}
      className={cn(VALUE, 'w-auto', align === 'end' && 'justify-end', numeric && 'num', mono && 'font-mono text-caption', !shown && 'text-fg-subtle', className)}
    >
      <span className="min-w-0 truncate">{shown ? (display ?? shown) : empty}</span>
    </Pressable>
  );
  return hint ? <Tooltip content={hint}>{button}</Tooltip> : button;
}

/** One of a few known values that reads as its value and opens a menu of the others on click. */
export function InlineChoice<T extends string>({
  value,
  options,
  label,
  onChange,
  className,
}: {
  value: T;
  options: readonly { value: T; label: ReactNode }[];
  label: string;
  onChange: (value: T) => void | Promise<void>;
  className?: string;
}) {
  const current = options.find((o) => o.value === value);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
      <Pressable aria-label={`${label}: ${typeof current?.label === 'string' ? current.label : value}. Change`} className={cn(VALUE, 'group/choice w-auto', className)}>
        <span className="min-w-0 truncate">{current?.label ?? value}</span>
        <ChevronDown className="size-3.5 shrink-0 text-fg-subtle opacity-0 transition-opacity duration-(--duration-fast) group-hover/choice:opacity-100 group-focus-visible/choice:opacity-100" aria-hidden />
      </Pressable>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-80 overflow-y-auto">
        <DropdownMenuRadioGroup value={value} onValueChange={(v) => void onChange(v as T)}>
          {options.map((o) => (
            <DropdownMenuRadioItem key={o.value} value={o.value}>
              {o.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
